import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router";
import { makeStyles, shorthands } from "@fluentui/react-components";
import {
    CalendarLtr20Regular,
    Clock20Regular,
    People20Regular,
    TextDescription20Regular,
    Save20Regular,
    Delete20Regular,
    Info16Regular,
    ArrowRepeatAll20Regular,
    Alert20Regular,
    Open16Regular,
    CheckmarkCircle24Regular,
    TaskListSquareLtr20Regular,
} from "@fluentui/react-icons";
import { useWindowManager } from "./WindowManagerContext";
import { useMailContext } from "../../context/MailContext";
import { calendarService, RoomDto, RecurrenceInput } from "../../services/calendarService";
import {
    calendarTerminService,
    type CalendarTerminNoteDto,
    type MailTerminLinkInput,
    type TerminMetaDto,
} from "../../services/calendarTerminService";
import { listAppUsers, AppUser } from "../../services/userService";
import { kontaktName, type KundeAuswahl } from "../calendar/KundePicker";
import {
    TerminKontaktePicker,
    type TerminKontaktAuswahl,
} from "../calendar/TerminKontaktePicker";
import { TerminSidecar } from "../calendar/TerminSidecar";
import { requestCalendarReload } from "../calendar/CalendarStateContext";
import RecurrencePicker, { type ReminderPreset } from "../calendar/RecurrencePicker";
import AttendeeChips from "../calendar/AttendeeChips";
import TerminFarbPicker from "../calendar/TerminFarbPicker";
import { terminFarbLabel } from "../calendar/terminBlock";
import { istVertraulich } from "../calendar/terminPrivatAnzeige";
import { ART_LABEL, zeitraumLabel } from "../calendar/peek";
import { SandboxedMailBody } from "../mail/SandboxedMailBody";
import {
    EmpfaengerVorschlagsListe,
    useEmpfaengerVorschlaege,
    type EmpfaengerVorschlagTexte,
} from "../mail/empfaengerVorschlaege";
import { openTerminFormWindow, type TerminPrefill } from "./openTerminFormWindow";
import {
    aufHalbeStundeAufrunden,
    endeNachStart,
    graphTerminGeaendert,
    ortAusKontakt,
    siehtAusWieHtml,
    sollOrtAutomatischSetzen,
    STANDARD_DAUER_MIN,
    mitVorgangTitel,
    teilnehmerVorschlagKontext,
    kontaktAusTeilnehmer,
    kontaktNachTeilnehmerEntfernt,
    autoKontakteBereinigen,
    type AutoKontakte,
    uhrzeitGueltig,
    vorgangLabel,
    type GraphFelder,
    type VorgangWahl,
} from "./terminFormLogik";
import { vorgangService } from "../../services/vorgangService";
import { vorlageAusTermin, type TerminVorlage } from "../calendar/terminVorlage";
import { REFINED } from "../risikoanalyseStep/tokens";
import "../calendar/calendar.css";
import { apiUrl } from "../../utils/api";
import { VorgangSuchListe, bindeAnTermin } from "../calendar/TerminVorgangDialog";
import { todoFuerNeuenTermin } from "../calendar/terminTodoBindung";
import { erbeVorgangsMails } from "../calendar/vorgangsMailsErben";
import { openVorgangCreateFlow } from "../vorgang/create/VorgangCreateFlowWindow";
import {
    type Attendee,
    ABWESENHEIT_LABEL,
    REMINDER_OPTIONS,
    minutesToPreset,
    ymd,
    parseZeit,
    dedupeAttendees,
    upsertAttendee,
    buildStartEnd,
    buildCreateEventInput,
    buildAttendeePayload,
    buildRecurrencePayload,
    reminderFromPreset,
    useInviteWarnings,
    useRaumVerfuegbarkeit,
    resolveCreateTargetMailbox,
    writeableCalendarMailboxes,
    canCreateTeams,
} from "../calendar/terminFormShared";
import { bestaetigen } from "../redesign/bestaetigen";
import { raumOptionText, raumWaehlbar, belegungText } from "../calendar/raumWahl";
import { interneAdressSet } from "../calendar/terminVorgang";
import type { EmailRecipientHit } from "../../services/graphMailService";

const FONT = REFINED.fontUI;

/** Hinweistexte der Teilnehmer-Vorschläge (Kontext = Kontakte/Vorgang des Termins). */
const TEILNEHMER_VORSCHLAG_TEXTE: EmpfaengerVorschlagTexte = {
    kontextTitel: "Vorschläge zu den Kontakten",
    alleGewaehlt:
        "Alle Adressen zu den Kontakten sind schon eingetragen. Für weitere Teilnehmer Namen oder Adresse eintippen.",
    keineAdresse:
        "Zu den Kontakten ist keine E-Mail-Adresse hinterlegt. Namen oder Adresse eintippen, um im ganzen Bestand zu suchen.",
};

/**
 * Art-Auswahl REDUZIERT (SP2): Kundentermin/Austausch/Sonstige — Abwesenheit
 * RAUS (eigener Weg), 'block' vergibt das System.
 *
 * „Sonstige" (PO 21.09.2026) ist die Auffang-Art: der Notartermin, die
 * Gerichtsverhandlung, der Gutachtertermin — weder Kundengespräch noch interne
 * Besprechung. Ein Kontakt darf daran hängen, deshalb bleibt der Kontakt-Picker
 * bei dieser Art sichtbar.
 */
type TerminArtChoice = "kundentermin" | "austausch" | "sonstige";

type Props = {
    windowId: string;
    mode: "create" | "edit";
    initialStart?: Date;
    initialEnd?: Date;
    allDay?: boolean;
    mailbox?: string | null;
    contactId?: number | null;
    contactName?: string | null;
    editEventId?: string;
    prefillEvent?: TerminPrefill;
    /** false ⇒ read-only (fremder Kalender ohne WRITE). */
    canEdit?: boolean;
    /** Detail-Remake „Austausch": neu angelegten Termin mit diesem Vorgang/Todo
     *  verknüpfen (best-effort, nur Create-Modus). */
    linkVorgangId?: number | null;
    /** Das To-do, an dem der neue Termin hängen soll (nur mit `linkVorgangId`). */
    linkTodoId?: number | null;
    /** K1/J3: den neu angelegten Termin sofort mit dieser Mail verknüpfen. */
    linkMail?: MailTerminLinkInput | null;
    /** Betreff-Vorbelegung für die Neu-Anlage. */
    initialSubject?: string;
    /** Veraltet: die Postfach-Auswahl steht in der Neuanlage immer ganz oben
     *  (PO 22.09.2026); die Prop bleibt nur, damit Aufrufer nicht brechen. */
    showMailboxHint?: boolean;
    /** Vg-SP6: vorbelegte Teilnehmer (NUR Create-Modus). Fertige
     *  `{ address, name }` vom Aufrufer — kein Key-Resolving hier. */
    initialAttendees?: { address: string; name?: string }[];
    /** T-269: bestehender Termin als Vorlage für die Neuanlage („Duplizieren…"). */
    vorlage?: TerminVorlage;
};

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: REFINED.white,
        color: REFINED.ink,
        fontFamily: FONT,
    },
    body: {
        flex: 1,
        overflowY: "auto",
        ...shorthands.padding("18px", "24px"),
        display: "flex",
        flexDirection: "column",
        rowGap: "14px",
    },
    field: { display: "flex", flexDirection: "column", rowGap: "6px" },
    fieldRowTwo: { display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: "14px" },
    label: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("5px"),
        fontSize: "0.85rem",
        color: REFINED.ink,
        fontWeight: 600,
    },
    requiredMark: { color: REFINED.mag, marginLeft: "2px" },
    input: {
        ...shorthands.border("1.5px", "solid", REFINED.line),
        borderRadius: "10px",
        ...shorthands.padding("10px", "12px"),
        fontFamily: "inherit",
        fontSize: "0.95rem",
        color: REFINED.ink,
        boxSizing: "border-box",
        transitionProperty: "border-color, box-shadow",
        transitionDuration: "140ms",
        ":hover": { ...shorthands.borderColor(REFINED.aub) },
        ":focus": { ...shorthands.borderColor(REFINED.aub), boxShadow: REFINED.focusRing, outlineStyle: "none" },
        ":disabled": { backgroundColor: REFINED.aub50, cursor: "not-allowed", color: REFINED.ink2 },
    },
    textarea: {
        ...shorthands.border("1.5px", "solid", REFINED.line),
        borderRadius: "10px",
        ...shorthands.padding("10px", "12px"),
        fontFamily: "inherit",
        fontSize: "0.95rem",
        color: REFINED.ink,
        minHeight: "72px",
        resize: "vertical",
        boxSizing: "border-box",
        transitionProperty: "border-color, box-shadow",
        transitionDuration: "140ms",
        ":hover": { ...shorthands.borderColor(REFINED.aub) },
        ":focus": { ...shorthands.borderColor(REFINED.aub), boxShadow: REFINED.focusRing, outlineStyle: "none" },
        ":disabled": { backgroundColor: REFINED.aub50, cursor: "not-allowed", color: REFINED.ink2 },
    },
    section: {
        display: "flex",
        flexDirection: "column",
        rowGap: "2px",
        ...shorthands.borderLeft("3px", "solid", REFINED.aub),
        paddingLeft: "12px",
        marginTop: "8px",
    },
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        fontWeight: 700,
        fontSize: "1.05rem",
        margin: 0,
        color: REFINED.ink,
    },
    sectionTitleIcon: { color: REFINED.aub, display: "inline-flex" },
    radioRow: { display: "flex", alignItems: "center", ...shorthands.gap("18px"), flexWrap: "wrap" },
    radioLabel: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("6px"),
        fontSize: "0.92rem",
        color: REFINED.ink,
        cursor: "pointer",
    },
    checkRow: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        fontSize: "0.92rem",
        color: REFINED.ink,
        cursor: "pointer",
    },
    attendeeRow: { display: "flex", alignItems: "center", ...shorthands.gap("8px") },
    // Lese-Übersicht im Edit (SP5, Plan 3.6.3): alle gesetzten Felder zuerst.
    overviewBox: {
        display: "flex",
        flexDirection: "column",
        rowGap: "6px",
        backgroundColor: REFINED.aub50,
        ...shorthands.border("1px", "solid", REFINED.aub200),
        borderRadius: "10px",
        ...shorthands.padding("12px", "14px"),
    },
    overviewTitle: {
        fontWeight: 700,
        fontSize: "0.95rem",
        margin: 0,
        color: REFINED.ink,
    },
    overviewRow: {
        display: "flex",
        alignItems: "baseline",
        ...shorthands.gap("8px"),
        fontSize: "0.9rem",
        color: REFINED.ink,
    },
    overviewLbl: {
        flexShrink: 0,
        minWidth: "96px",
        fontSize: "0.8rem",
        color: REFINED.ink2,
        fontWeight: 600,
    },
    overviewNote: {
        fontSize: "0.85rem",
        color: REFINED.ink,
        backgroundColor: REFINED.white,
        borderRadius: "6px",
        ...shorthands.padding("6px", "8px"),
    },
    // HTML-Beschreibung (SP5/A9): gerenderter, sanitisierter Body mit viel Platz.
    htmlBodyBox: {
        ...shorthands.border("1.5px", "solid", REFINED.line),
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: REFINED.white,
    },
    // Erfolgs-Zwischenschritt nach der Neu-Anlage (SP5/A11).
    successBox: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        rowGap: "10px",
        textAlign: "center",
        ...shorthands.padding("48px", "24px"),
    },
    successIcon: { color: "#2e7d32", display: "inline-flex" },
    successTitle: { fontWeight: 700, fontSize: "1.15rem", margin: 0, color: REFINED.ink },
    successText: { fontSize: "0.95rem", color: REFINED.ink, margin: 0 },
    successHint: { fontSize: "0.85rem", color: REFINED.ink2, margin: 0, maxWidth: "420px" },
    addBtn: {
        ...shorthands.padding("9px", "16px"),
        borderRadius: "10px",
        ...shorthands.border("1.5px", "solid", REFINED.line),
        backgroundColor: REFINED.white,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "0.85rem",
        fontWeight: 600,
        color: REFINED.aub,
        whiteSpace: "nowrap",
        ":hover": { ...shorthands.borderColor(REFINED.aub) },
        ":disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    warnBox: {
        fontSize: "0.85rem",
        color: "#8a5a00",
        backgroundColor: "#fff7e6",
        ...shorthands.border("1px", "solid", "#f0c879"),
        borderRadius: "8px",
        ...shorthands.padding("8px", "12px"),
    },
    readOnlyBanner: {
        fontSize: "0.85rem",
        color: REFINED.ink2,
        backgroundColor: REFINED.aub50,
        ...shorthands.border("1px", "solid", REFINED.aub200),
        borderRadius: "8px",
        ...shorthands.padding("8px", "12px"),
        fontWeight: 600,
    },
    // SP2 (Review 2026-07-07): dezenter Postfach-Hinweis im Create-Modus,
    // wenn auf ein anderes WRITE-Postfach zurückgefallen wurde.
    mailboxHint: {
        fontSize: "0.82rem",
        color: REFINED.ink2,
        backgroundColor: REFINED.aub50,
        ...shorthands.border("1px", "solid", REFINED.aub200),
        borderRadius: "8px",
        ...shorthands.padding("6px", "12px"),
    },
    seriesBanner: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        ...shorthands.gap("8px"),
        fontSize: "0.85rem",
        color: REFINED.aub,
        backgroundColor: REFINED.aub50,
        ...shorthands.border("1px", "solid", REFINED.aub200),
        borderRadius: "8px",
        ...shorthands.padding("8px", "12px"),
        fontWeight: 600,
    },
    seriesOpenBtn: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("6px"),
        ...shorthands.padding("8px", "14px"),
        borderRadius: "8px",
        ...shorthands.borderWidth("0"),
        backgroundColor: REFINED.aub,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "0.82rem",
        fontWeight: 600,
        color: REFINED.white,
        whiteSpace: "nowrap",
        ":hover": { backgroundColor: REFINED.aub700 },
    },
    sidecarSection: {
        marginTop: "10px",
        ...shorthands.borderTop("1px", "solid", REFINED.line),
        paddingTop: "16px",
    },
    footerBar: {
        flexShrink: 0,
        display: "flex",
        backgroundColor: REFINED.white,
        ...shorthands.borderTop("1px", "solid", REFINED.line),
    },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        ...shorthands.padding("14px", "24px"),
        width: "100%",
        boxSizing: "border-box",
    },
    footerRight: { display: "flex", alignItems: "center", columnGap: "12px" },
    ghostBtn: {
        ...shorthands.padding("10px", "20px"),
        borderRadius: "10px",
        ...shorthands.border("1.5px", "solid", REFINED.line),
        backgroundColor: REFINED.white,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "0.9rem",
        fontWeight: 600,
        color: REFINED.ink,
        ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
    },
    deleteBtn: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("6px"),
        ...shorthands.padding("10px", "18px"),
        borderRadius: "10px",
        ...shorthands.border("1.5px", "solid", REFINED.line),
        backgroundColor: REFINED.white,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "0.9rem",
        fontWeight: 600,
        color: REFINED.err,
        ":hover": { ...shorthands.borderColor(REFINED.err) },
        ":disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    primaryBtn: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("6px"),
        ...shorthands.padding("10px", "24px"),
        borderRadius: "10px",
        ...shorthands.borderWidth("0"),
        backgroundColor: REFINED.aub,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "0.9rem",
        fontWeight: 600,
        color: REFINED.white,
        boxShadow: REFINED.btnShadow,
        ":hover": { backgroundColor: REFINED.aub700 },
        ":disabled": { opacity: 0.5, cursor: "not-allowed", boxShadow: "none" },
    },
    errorBox: {
        backgroundColor: REFINED.errBg,
        color: REFINED.err,
        ...shorthands.padding("8px", "12px"),
        borderRadius: "8px",
        fontSize: "0.82rem",
    },
    reqHint: {
        display: "flex",
        alignItems: "center",
        ...shorthands.gap("6px"),
        fontSize: "0.82rem",
        color: REFINED.err,
        fontWeight: 600,
    },
});

const TerminFormWindow: React.FC<Props> = ({
    windowId,
    mode,
    initialStart,
    initialEnd,
    allDay: initialAllDay,
    mailbox: mailboxParam,
    contactId,
    contactName,
    editEventId,
    prefillEvent,
    canEdit = true,
    linkVorgangId = null,
    linkTodoId = null,
    linkMail = null,
    initialSubject,
    initialAttendees,
    vorlage,
}) => {
    const styles = useStyles();
    const { openWindow, closeWindow, minimizeWindow, setWindowTitle, setWindowDirty } =
        useWindowManager();
    const { currentMailbox, mailboxes, ownMailbox } = useMailContext();
    const navigate = useNavigate();

    const isEdit = mode === "edit";

    // Ziel-Postfach: Edit schreibt in das Postfach des Events (prefill/param).
    // Create: auswählbar GANZ OBEN (PO 22.09.2026: „bei neuen Termin immer die
    // eigene Adresse verwenden und ganz oben kann man das noch ändern").
    // Default = EIGENES WRITE-Kalender-Postfach, ein explizit übergebenes
    // `mailbox` gewinnt (siehe `resolveCreateTargetMailbox`).
    const [createMailbox, setCreateMailbox] = useState<string>(() =>
        resolveCreateTargetMailbox(mailboxes, ownMailbox, currentMailbox, mailboxParam),
    );
    // Hat der User das Postfach BEWUSST geändert? Dann zieht der Kaltstart-
    // Nachzieh-Effekt (unten) nichts mehr nach.
    const userTouchedMailboxRef = useRef(false);
    useEffect(() => {
        // Kaltstart: Öffnet das Fenster, BEVOR MailContext.mailboxes geladen ist,
        // fiele die Auflösung auf currentMailbox (evtl. cbb@) zurück. Sobald die
        // Liste da ist, neu auflösen — solange der User nicht selbst gewählt hat.
        if (isEdit || userTouchedMailboxRef.current || mailboxes.length === 0) return;
        const resolved = resolveCreateTargetMailbox(mailboxes, ownMailbox, currentMailbox, mailboxParam);
        setCreateMailbox((prev) => (prev === resolved ? prev : resolved));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mailboxes]);
    const writeMailboxes = useMemo(() => writeableCalendarMailboxes(mailboxes), [mailboxes]);
    const targetMailbox = (isEdit ? mailboxParam : createMailbox) ?? currentMailbox ?? "";
    // Teams nur, wenn das Ziel-Postfach Online-Meetings anlegen darf.
    const teamsAllowed = useMemo(
        () => canCreateTeams(mailboxes, targetMailbox),
        [mailboxes, targetMailbox],
    );

    // SP2 (Review 2026-07-07): Anzeigename des Ziel-Postfachs.
    const targetMailboxLabel = useMemo(() => {
        const mb = mailboxes.find(
            (m) => m.mailboxAddress.toLowerCase() === targetMailbox.toLowerCase(),
        );
        return mb?.displayName || targetMailbox;
    }, [mailboxes, targetMailbox]);

    const [rooms, setRooms] = useState<RoomDto[]>([]);
    const [appUsers, setAppUsers] = useState<AppUser[]>([]);

    const [subject, setSubject] = useState(prefillEvent?.title ?? initialSubject ?? "");
    const [body, setBody] = useState("");
    // SP5/A9 (Entscheidung 64): HTML-Bodies (Teams-Einladungen) werden lesend
    // gerendert; die Textarea ist der Plain-Edit. Der Body wird beim PATCH nur
    // gesendet, wenn der User ihn tatsächlich geändert hat (bodyDirty) — sonst
    // würde ein reiner Titel-/Zeit-Edit das Original-HTML zerstören.
    const [bodyContentType, setBodyContentType] = useState<string | null>(null);
    const [bodyDirty, setBodyDirty] = useState(false);
    const [bodyEditMode, setBodyEditMode] = useState(false);
    const [location, setLocation] = useState(prefillEvent?.location ?? "");
    // Lese-Übersicht im Edit (SP5): Organisator/Teams-Link/Verknüpfungen/Notizen.
    const [organizer, setOrganizer] = useState<{ name: string; address: string } | null>(null);
    const [onlineMeetingUrl, setOnlineMeetingUrl] = useState<string | null>(
        prefillEvent?.onlineMeetingUrl ?? null,
    );
    const [linkedVorgangId, setLinkedVorgangId] = useState<number | null>(null);
    const [linkedTodoId, setLinkedTodoId] = useState<number | null>(null);
    const [vorhandeneNotizen, setVorhandeneNotizen] = useState<CalendarTerminNoteDto[]>([]);
    // SP5/A11: Erfolgs-Zwischenschritt nach der Neu-Anlage („Weitere Optionen").
    const [angelegt, setAngelegt] = useState<{ id: string } | null>(null);
    /**
     * K3 — der nach dem Speichern frisch gelesene Sidecar-Stand. Er wird
     * bewusst NACHGELADEN statt aus dem Formular abgeleitet: nur so sieht der
     * Nutzer, was wirklich am Server steht.
     */
    const [sidecarMeta, setSidecarMeta] = useState<TerminMetaDto | null>(null);

    const [allDay, setAllDay] = useState<boolean>(prefillEvent?.allDay ?? initialAllDay ?? false);
    // Online-Meeting (Teams) nur bei Create — Graph erlaubt nachträgliches
    // Umschalten nicht zuverlässig (Bestand OutlookTermine).
    const [isOnlineMeeting, setIsOnlineMeeting] = useState(false);

    const [attendeeInput, setAttendeeInput] = useState("");
    // Vg-SP6: im Create-Modus vom Aufrufer vorbelegte Teilnehmer (Austausch aus
    // Vorgang/Todo) — fertige { address, name }, über die GETEILTE Dedupe-Logik
    // (terminFormShared, identisch zum Light-Dialog). Im Edit überschreibt das
    // getEvent-Prefill diesen Startwert ohnehin.
    const [attendees, setAttendees] = useState<Attendee[]>(() =>
        isEdit ? [] : dedupeAttendees(initialAttendees),
    );
    const [roomAddress, setRoomAddress] = useState<string>("");
    const [attendeesDirty, setAttendeesDirty] = useState(false);

    // Neuanlage ohne Vorgabe: nächste halbe Stunde, Ende 30 Minuten später
    // (PO 28.09.2026 — vorher 10:00–11:00 fest bzw. eine Stunde).
    const initStart =
        prefillEvent?.start ?? initialStart ?? (isEdit ? null : aufHalbeStundeAufrunden(new Date()));
    const initEnd =
        prefillEvent?.end ??
        initialEnd ??
        (initStart && !isEdit ? new Date(initStart.getTime() + STANDARD_DAUER_MIN * 60 * 1000) : null);
    const [startDate, setStartDate] = useState<Date | null>(initStart);
    const [startTime, setStartTime] = useState(initStart ? moment(initStart).format("HH:mm") : "10:00");
    const [endDate, setEndDate] = useState<Date | null>(initEnd);
    const [endTime, setEndTime] = useState(initEnd ? moment(initEnd).format("HH:mm") : "10:30");

    /**
     * „von" geändert ⇒ „bis" erstmal 30 Minuten dahinter (PO 28.09.2026).
     * Greift nur beim Bearbeiten durch den Nutzer — das Vorbelegen beim Öffnen
     * lässt die echte Dauer eines bestehenden Termins in Ruhe.
     */
    const aendereStartZeit = (zeit: string) => {
        setStartTime(zeit);
        if (!startDate || !zeit) return;
        const ende = endeNachStart(startDate, zeit);
        setEndDate(ende.endDate);
        setEndTime(ende.endTime);
    };
    /**
     * Start-Datum geändert ⇒ Ende auf denselben Tag (bzw. denselben Abstand in
     * Tagen bei mehrtägigen Terminen) mitnehmen; die Uhrzeit folgt der
     * 30-Minuten-Regel. Ohne das stünde das Ende nach dem Verschieben auf einen
     * späteren Tag VOR dem Start.
     */
    const aendereStartDatum = (d: Date | null) => {
        setStartDate(d);
        if (!d) return;
        if (allDay) {
            const tage =
                startDate && endDate
                    ? Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 86400000))
                    : 0;
            const e = new Date(d);
            e.setDate(e.getDate() + tage);
            setEndDate(e);
            return;
        }
        const ende = endeNachStart(d, startTime);
        setEndDate(ende.endDate);
        setEndTime(ende.endTime);
    };

    // Art: im Edit aus dem Sidecar, in der Neuanlage „Kundentermin", wenn der
    // Aufrufer einen Kontakt mitgibt (Kontaktakte, Vorgang), sonst „Austausch".
    const [terminArt, setTerminArtState] = useState<TerminArtChoice>(
        !isEdit && contactId ? "kundentermin" : "austausch",
    );
    // Hat der Nutzer die Art ausdrücklich gewählt? Nur dann schreibt die
    // Neuanlage sie — sonst bleibt die Server-Auto-Klassifikation aus den
    // Teilnehmern (classifyFromAttendees im POST-Pfad) am Zug.
    const [artGewaehlt, setArtGewaehlt] = useState(false);
    const setTerminArt = (art: TerminArtChoice) => {
        setTerminArtState(art);
        setArtGewaehlt(true);
    };
    const [vorgangWahl, setVorgangWahl] = useState<VorgangWahl>(() =>
        !isEdit && linkVorgangId != null
            ? { kind: "existing", vorgangId: linkVorgangId, label: vorgangLabel(linkVorgangId, null) }
            : { kind: "none" },
    );
    const [vorgangPickerOpen, setVorgangPickerOpen] = useState(false);
    /**
     * Die Kontakte des Termins (PO 21.09.2026). Der ERSTE ist der Hauptkontakt:
     * er landet in `kundeId` und trägt Fenstertitel, Termin-Art und
     * Kalender-Optik. `kunde` ist deshalb nur noch die abgeleitete Spitze dieser
     * Liste und keine zweite Quelle der Wahrheit — sonst liefen Chip-Reihe und
     * Titel bei jedem Umsortieren auseinander.
     */
    const [kontakte, setKontakte] = useState<TerminKontaktAuswahl[]>(
        contactId ? [{ id: contactId, name: contactName?.trim() || `Kontakt ${contactId}` }] : [],
    );
    const kunde: KundeAuswahl | null = kontakte[0]
        ? { id: kontakte[0].id, name: kontakte[0].name }
        : null;
    // SP6/A10: individuelle Termin-Farbe (Preset, KMVS-intern) — nur im Edit
    // sichtbar (Create bleibt schlank, A11); gespeichert wird beim Save, aber
    // nur wenn der User sie tatsächlich angefasst hat (farbeDirty).
    const [farbe, setFarbe] = useState<string | null>(null);
    const [farbeDirty, setFarbeDirty] = useState(false);
    // Privater Termin (PO 27.07.2026). `privatDirty` wie bei der Farbe: die
    // Markierung nur senden, wenn der User sie angefasst hat — sonst
    // ueberschriebe ein reiner Zeit-Edit eine in Outlook gesetzte Markierung.
    const [istPrivat, setIstPrivat] = useState(false);
    const [privatDirty, setPrivatDirty] = useState(false);

    // --- Serientermin + Erinnerung (SP4) ---
    const [recurrence, setRecurrence] = useState<RecurrenceInput | null>(null);
    const [reminderPreset, setReminderPreset] = useState<ReminderPreset>("none");
    // Serie wird nur gesendet, wenn der User den Picker tatsächlich angefasst hat
    // (PATCH-Semantik: weglassen = unangetastet, null = auflösen).
    const [recurrenceDirty, setRecurrenceDirty] = useState(false);
    // Serieninstanz-Gating (Entscheidung D): occurrence/exception ⇒ read-only,
    // Master per "Ganze Reihe bearbeiten" öffnen.
    const [seriesInstance, setSeriesInstance] = useState<{ masterId: string | null } | null>(null);

    const isSeriesInstance = !!seriesInstance;
    // read-only, wenn fremder Kalender ODER Serieninstanz (D).
    const readOnly = !canEdit || isSeriesInstance;

    // Seit PO 28.09.2026 steht das Art-Radio (Kundentermin/Austausch/Sonstige)
    // wieder in BEIDEN Modi — die Neuanlage läuft wieder im großen Fenster.
    const effectiveArt: TerminArtChoice = terminArt;

    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Snapshot des Initialzustands → Dirty-Vergleich; in Edit nach dem Nachladen
    // aktualisiert, damit das reine Vorbelegen das Fenster nicht „dirty" macht.
    const initialSnapshotRef = useRef<string>("");
    // T-269: mit Vorlage steht die Baseline erst NACH dem Laden der Vorlage.
    const [initialReady, setInitialReady] = useState(!isEdit && !vorlage);
    /**
     * Automatisches Vorbelegen nach dem Öffnen (T-267 Ort aus dem Kontakt,
     * T-268 Vorgangsbetreff, T-269 Vorlage): kommt der Wert asynchron, nachdem
     * die Dirty-Baseline schon steht, wäre das Fenster sofort „ungespeichert".
     * Hat der Nutzer bis dahin nichts geändert (aktueller Snapshot ==
     * Baseline), zieht der Dirty-Effekt die Baseline deshalb einmal nach.
     */
    const snapshotRef = useRef<string>("");
    const baselineNachziehenRef = useRef(false);
    const vorbelegen = (anwenden: () => void) => {
        if (initialSnapshotRef.current && snapshotRef.current === initialSnapshotRef.current) {
            baselineNachziehenRef.current = true;
        }
        anwenden();
    };

    // T-267: „Ort" aus der Standardanschrift des Hauptkontakts — nur in der
    // Neuanlage und nur, solange der Nutzer „Ort" nicht selbst angefasst hat.
    const ortVomNutzerRef = useRef(false);
    const autoOrtRef = useRef<string | null>(null);
    const locationRef = useRef(location);
    locationRef.current = location;
    const hauptKontaktId = kontakte[0]?.id ?? null;
    useEffect(() => {
        if (isEdit) return;
        const darf = () =>
            sollOrtAutomatischSetzen({
                isEdit,
                ortVomNutzer: ortVomNutzerRef.current,
                aktuellerOrt: locationRef.current,
                letzterAutoOrt: autoOrtRef.current,
            });
        const uebernehmen = (ort: string) => {
            if (!darf() || ort === locationRef.current) return;
            vorbelegen(() => setLocation(ort));
            autoOrtRef.current = ort || null;
        };
        if (hauptKontaktId == null) {
            // Hauptkontakt entfernt: einen automatisch gesetzten Ort mitnehmen.
            if (autoOrtRef.current) uebernehmen("");
            return;
        }
        const ctrl = new AbortController();
        fetch(apiUrl(`contact/${hauptKontaktId}`, { include: "" }), { signal: ctrl.signal })
            .then((r) => (r.ok ? r.json() : null))
            .then((c) => {
                if (ctrl.signal.aborted) return;
                // Ohne Anschrift: nur einen alten Automatik-Ort räumen.
                const ort = ortAusKontakt(c);
                if (ort || autoOrtRef.current) uebernehmen(ort);
            })
            .catch(() => {
                /* Abbruch oder kein Zugriff (403/404): „Ort" bleibt, wie er ist. */
            });
        return () => ctrl.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, hauptKontaktId]);

    // T-268: Der vorbelegte Vorgang (aus dem Vorgang heraus angelegt) kommt nur
    // mit seiner Nummer an — den Betreff gezielt nachladen (gecacht, aus der
    // Vorgangsseite meist schon warm). Scheitert der Abruf (gelöscht, kein
    // Leserecht), bleibt „Vorgang #98" stehen.
    const vorgangOhneTitel =
        !isEdit &&
        vorgangWahl.kind === "existing" &&
        vorgangWahl.label === vorgangLabel(vorgangWahl.vorgangId, null)
            ? vorgangWahl.vorgangId
            : null;
    useEffect(() => {
        if (vorgangOhneTitel == null) return;
        let abgebrochen = false;
        vorgangService
            .getVorgang(vorgangOhneTitel)
            .then((v) => {
                if (abgebrochen || !(v?.titel ?? "").trim()) return;
                vorbelegen(() =>
                    setVorgangWahl((w) => mitVorgangTitel(w, vorgangOhneTitel, v.titel)),
                );
            })
            .catch(() => {
                /* Rückfall: die Nummer bleibt stehen. */
            });
        return () => {
            abgebrochen = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vorgangOhneTitel]);

    // Räume + Kollegen einmalig laden.
    useEffect(() => {
        let cancelled = false;
        listAppUsers()
            .then((list) => {
                if (!cancelled) setAppUsers(list);
            })
            .catch((err) => console.warn("[TerminFormWindow] users load failed", err));
        calendarService
            .listRooms()
            .then((list) => {
                if (!cancelled) setRooms(list);
            })
            .catch((err) => console.warn("[TerminFormWindow] rooms load failed", err));
        return () => {
            cancelled = true;
        };
    }, []);

    // Edit-Prefill: Body + strukturierte Teilnehmer (getEvent) und Art/Kunde
    // (getMeta). Abwesenheits-Altbestand wird als 'austausch' ANGEZEIGT (nicht
    // zurückgeschrieben, solange der User nichts ändert).
    useEffect(() => {
        if (!isEdit || !editEventId || !targetMailbox) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const detail = await calendarService.getEvent(targetMailbox, editEventId);
                if (cancelled) return;
                if (detail.body?.content) setBody(detail.body.content);
                // SP5/A9: contentType merken (steuert Lese-Rendering vs. Textarea).
                setBodyContentType(detail.body?.contentType ?? null);
                // SP5-Übersicht: Organisator + Teams-Link (Screenshot-Gap A16/3.6.3).
                setOrganizer(detail.organizer ?? null);
                setOnlineMeetingUrl(detail.onlineMeetingUrl ?? null);
                const raw = detail.attendees || [];
                setAttendees(
                    raw
                        .filter((a) => a.type !== "resource")
                        .map((a) => ({ address: a.address, name: a.name?.trim() || a.address })),
                );
                setRoomAddress(raw.find((a) => a.type === "resource")?.address || "");
                setIsOnlineMeeting(!!detail.onlineMeetingUrl);

                // Serieninstanz-Gating (D): occurrence/exception ⇒ read-only,
                // "Ganze Reihe bearbeiten" lädt den Master.
                if (detail.type === "occurrence" || detail.type === "exception") {
                    setSeriesInstance({ masterId: detail.seriesMasterId });
                }
                // Serie + Erinnerung vorbelegen — HIER wirkt das BE-Read-Mapping
                // (Entscheidung G): ohne es bliebe der Picker leer und ein Save
                // würde die Serie zerstören.
                // Privat-Markierung vorbelegen (PO 27.07.2026): ohne sie waere
                // das Haeckchen eine Einbahnstrasse — gesetzt liesse es sich nur
                // noch in Outlook wieder entfernen.
                setIstPrivat(istVertraulich(detail.sensitivity));
                setRecurrence(detail.recurrence ?? null);
                setReminderPreset(
                    minutesToPreset(detail.isReminderOn, detail.reminderMinutesBeforeStart),
                );
            } catch (err) {
                console.warn("[TerminFormWindow] getEvent (Prefill) fehlgeschlagen", err);
            }
            // SP5-Übersicht: vorhandene Notizen (best-effort, auch read-only).
            try {
                const notizen = await calendarTerminService.listNotes(targetMailbox, editEventId);
                if (!cancelled) setVorhandeneNotizen(notizen);
            } catch {
                /* kein Sidecar / kein Zugriff → keine Notizen in der Übersicht */
            }
            try {
                const meta = await calendarTerminService.getMeta(targetMailbox, editEventId);
                if (cancelled) return;
                // SP5-Übersicht: Vorgang-/Todo-Verknüpfung anzeigen (Lese-Sektion).
                setLinkedVorgangId(meta.vorgangId);
                setLinkedTodoId(meta.todoId);
                // SP6/A10: individuelle Farbe vorbelegen (Picker + Übersicht).
                setFarbe(meta.farbe ?? null);
                // Kundentermin und Sonstige übernehmen — abwesenheit/block
                // werden weiterhin als 'austausch' angezeigt (Abwesenheit ist
                // aus dieser UI raus, 'block' vergibt das System).
                if (meta.terminArt === "kundentermin" || meta.terminArt === "sonstige") {
                    setTerminArtState(meta.terminArt);
                }
                // Zugewiesene Kontakte (PO 21.09.2026) — EIN Request für die
                // ganze Liste, Hauptkontakt zuerst. Der Fallback auf `kundeId`
                // deckt Sidecars ab, deren Zuweisung (noch) leer ist: der
                // Nachzieh-Lauf der Migration erwischt nur, was zur Laufzeit
                // schon klassifiziert war.
                try {
                    const { kontakte: zugewiesen } = await calendarTerminService.getTerminKontakte(
                        targetMailbox,
                        editEventId,
                    );
                    if (cancelled) return;
                    if (zugewiesen.length > 0) {
                        setKontakte(
                            zugewiesen.map((k) => ({ id: k.contactId, name: k.name })),
                        );
                    } else if (meta.kundeId != null) {
                        const r = await fetch(apiUrl(`contact/${meta.kundeId}`));
                        const c = r.ok ? await r.json() : null;
                        if (!cancelled && c?.id) {
                            setKontakte([{ id: c.id, name: kontaktName(c) }]);
                        }
                    }
                } catch {
                    /* Kontakt-Namen best-effort; die Zuweisung bleibt unberührt */
                }
            } catch {
                /* kein Sidecar / kein Zugriff → unklassifiziert (austausch) */
            } finally {
                if (!cancelled) {
                    setInitialReady(true);
                    setAttendeesDirty(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isEdit, editEventId, targetMailbox]);

    // T-269 „Duplizieren…": Vorlage nachladen und in die Felder der Neuanlage
    // übernehmen — Muster wie das Edit-Prefill, aber alles best-effort: was
    // nicht lädt (kein Zugriff, Kontakt-Ebene), bleibt leer. Zeiten und Betreff
    // stehen schon über den Öffner im Formular. Die Dirty-Baseline entsteht
    // erst danach (initialReady), damit die Kopie nicht sofort „ungespeichert" ist.
    useEffect(() => {
        if (isEdit || !vorlage) return;
        let cancelled = false;
        (async () => {
            const laden = async <T,>(f: () => Promise<T>): Promise<T | null> => {
                if (vorlage.ueberKontakt) return null;
                try {
                    return await f();
                } catch {
                    return null;
                }
            };
            try {
                const [detail, meta, zuweisung] = await Promise.all([
                    laden(() => calendarService.getEvent(vorlage.mailbox, vorlage.eventId)),
                    laden(() => calendarTerminService.getMeta(vorlage.mailbox, vorlage.eventId)),
                    laden(() => calendarTerminService.getTerminKontakte(vorlage.mailbox, vorlage.eventId)),
                ]);
                if (cancelled) return;
                const w = vorlageAusTermin(vorlage, detail, meta, zuweisung?.kontakte ?? null);
                if (w.subject) setSubject(w.subject);
                setLocation(w.location);
                setBody(w.body);
                setBodyContentType(w.bodyContentType);
                setIsOnlineMeeting(w.isOnlineMeeting);
                setAttendees(dedupeAttendees(w.attendees));
                setRoomAddress(w.roomAddress);
                setReminderPreset(w.reminderPreset);
                setIstPrivat(w.istPrivat);
                if (w.terminArt) {
                    setTerminArtState(w.terminArt);
                    setArtGewaehlt(true);
                }
                if (w.kontakte.length > 0) setKontakte(w.kontakte);
                if (w.farbe) {
                    setFarbe(w.farbe);
                    setFarbeDirty(true);
                }
                // Ein Vorgang aus dem Aufrufer (Auswahlmodus) hat Vorrang.
                if (w.vorgang && linkVorgangId == null) {
                    setVorgangWahl({
                        kind: "existing",
                        vorgangId: w.vorgang.id,
                        label: vorgangLabel(w.vorgang.id, w.vorgang.titel),
                    });
                }
            } finally {
                if (!cancelled) setInitialReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Die Vorlage gilt nur beim Öffnen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Interne Teilnehmer + Einladungs-Warnung (TER-D6) — GETEILTER Hook
    // (terminFormShared, identisch zum Light-Dialog): nur bei 'austausch' mit
    // internen Teilnehmern; die Ganztags-+1-Tag-Zeitrechnung teilt er mit dem
    // Anlage-Pfad (buildStartEnd), damit Warnung und Grid nie auseinanderlaufen.
    const { warnings: inviteWarnings, interneTeilnehmer } = useInviteWarnings({
        aktiv: effectiveArt === "austausch",
        attendees,
        appUsers,
        startDate,
        endDate,
        startTime,
        endTime,
        allDay,
    });

    // Live-Titel.
    useEffect(() => {
        const title =
            effectiveArt === "kundentermin" && kunde
                ? `${subject.trim() || "Kundentermin"} · ${kunde.name}`
                : subject.trim() || (isEdit ? "Termin" : "Neuer Termin");
        setWindowTitle(windowId, title);
    }, [subject, effectiveArt, kunde, windowId, setWindowTitle, isEdit]);

    // Dirty-Tracking: Snapshot aller Form-States. In Edit erst NACH dem
    // Nachladen als Baseline merken, sonst meldet das Prefill fälschlich dirty.
    const snapshot = useMemo(
        () =>
            JSON.stringify({
                subject,
                body,
                location,
                allDay,
                isOnlineMeeting,
                attendees,
                roomAddress,
                startDate: startDate ? ymd(startDate) : null,
                startTime,
                endDate: endDate ? ymd(endDate) : null,
                endTime,
                terminArt,
                // Die ganze Zuweisung, nicht nur der Hauptkontakt: einen
                // zweiten Kontakt anzuhängen ist eine Änderung, die das
                // Fenster als „ungespeichert" führen muss.
                kontaktIds: kontakte.map((k) => k.id),
                recurrence,
                reminderPreset,
                farbe,
                createMailbox: isEdit ? null : createMailbox,
                vorgangWahl,
            }),
        [subject, body, location, allDay, isOnlineMeeting, attendees, roomAddress, startDate, startTime, endDate, endTime, terminArt, kontakte, recurrence, reminderPreset, farbe, isEdit, createMailbox, vorgangWahl],
    );

    // Nur die Felder des OUTLOOK-Termins — Grundlage der Entscheidung, ob
    // „Speichern" Graph überhaupt anfassen darf (siehe graphTerminGeaendert).
    const graphFelder: GraphFelder = useMemo(
        () => ({
            subject,
            startDate: startDate ? ymd(startDate) : null,
            startTime,
            endDate: endDate ? ymd(endDate) : null,
            endTime,
            allDay,
            location,
            reminderPreset,
        }),
        [subject, startDate, startTime, endDate, endTime, allDay, location, reminderPreset],
    );
    const initialGraphFelderRef = useRef<GraphFelder | null>(null);
    // Ausgangsstand der KMVS-Felder: Art und Kontakte werden im Edit nur
    // geschrieben, wenn sie sich geändert haben.
    const initialKmvsRef = useRef<{ art: TerminArtChoice; kontaktIds: string } | null>(null);

    snapshotRef.current = snapshot;

    useEffect(() => {
        if (!initialReady) return;
        if (!initialSnapshotRef.current || baselineNachziehenRef.current) {
            baselineNachziehenRef.current = false;
            initialSnapshotRef.current = snapshot;
            initialGraphFelderRef.current = graphFelder;
            initialKmvsRef.current = {
                art: terminArt,
                kontaktIds: kontakte.map((k) => k.id).join(","),
            };
            setWindowDirty(windowId, false);
            return;
        }
        setWindowDirty(windowId, snapshot !== initialSnapshotRef.current);
        // graphFelder/terminArt/kontakte stecken bereits im Snapshot.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshot, initialReady, windowId, setWindowDirty]);

    const addAttendee = (address: string, name?: string) => {
        if (!address.trim()) return;
        // Geteilte Dedupe-Logik (terminFormShared) — identisch zum Light-Dialog.
        setAttendees((prev) => upsertAttendee(prev, address, name));
        setAttendeeInput("");
        setAttendeesDirty(true);
    };

    /**
     * Kontaktliste setzen — geteilter Weg für die Kontaktauswahl von Hand und
     * die Übernahme aus einem gewählten Teilnehmer. Neuanlage ohne
     * ausdrückliche Art: der erste Kontakt macht daraus sichtbar einen
     * Kundentermin (wie bisher). Die Anschrift (T-267) zieht der Effekt am
     * Hauptkontakt nach — nur in einen leeren bzw. selbst gesetzten „Ort".
     */
    const kontakteSetzen = (naechste: TerminKontaktAuswahl[]) => {
        setKontakte(naechste);
        if (!isEdit && !artGewaehlt && naechste.length > 0 && terminArt === "austausch") {
            setTerminArtState("kundentermin");
        }
    };
    // Kontakte, die erst die Wahl eines Teilnehmers zugewiesen hat (siehe
    // kontaktAusTeilnehmer) — nur sie nimmt das Entfernen des Teilnehmers mit.
    const autoKontakteRef = useRef<AutoKontakte>({});
    const kontakteVonHand = (naechste: TerminKontaktAuswahl[]) => {
        autoKontakteRef.current = autoKontakteBereinigen(autoKontakteRef.current, naechste);
        kontakteSetzen(naechste);
    };

    const removeAttendee = (address: string) => {
        setAttendees((prev) => prev.filter((a) => a.address !== address));
        setAttendeesDirty(true);
        const r = kontaktNachTeilnehmerEntfernt(kontakte, autoKontakteRef.current, address);
        autoKontakteRef.current = r.auto;
        if (r.kontakte.length !== kontakte.length) kontakteSetzen(r.kontakte);
    };

    // Adressvorschläge im Teilnehmerfeld — wie beim Mail-Verfassen (geteilte
    // Logik aus empfaengerVorschlaege): beim Klick die Adressen der Kontakte
    // und des Vorgangs, beim Tippen alle in KMVS hinterlegten Adressen.
    const vorschlagVorgangId =
        vorgangWahl.kind === "existing" ? vorgangWahl.vorgangId : linkedVorgangId;
    const [vorschlagVorgang, setVorschlagVorgang] = useState<{
        id: number;
        kundeId: number | null;
        contactIds: number[];
    } | null>(null);
    useEffect(() => {
        if (vorschlagVorgangId == null) return;
        let abgebrochen = false;
        vorgangService
            .getVorgang(vorschlagVorgangId)
            .then((v) => {
                if (abgebrochen || !v) return;
                setVorschlagVorgang({
                    id: vorschlagVorgangId,
                    kundeId: v.kunde?.id ?? null,
                    contactIds: v.contactIds ?? [],
                });
            })
            .catch(() => {
                /* Ohne Vorgangsdaten bleiben die Kontakte des Termins. */
            });
        return () => {
            abgebrochen = true;
        };
    }, [vorschlagVorgangId]);
    const vorschlagKontext = teilnehmerVorschlagKontext(
        kontakte.map((k) => k.id),
        vorschlagVorgang && vorschlagVorgang.id === vorschlagVorgangId ? vorschlagVorgang : null,
    );
    const [teilnehmerListeOffen, setTeilnehmerListeOffen] = useState(false);
    const teilnehmerAnkerRef = useRef<HTMLDivElement | null>(null);
    const teilnehmerVorschlaege = useEmpfaengerVorschlaege({
        aktiv: !readOnly,
        offen: teilnehmerListeOffen,
        eingabe: attendeeInput,
        kontextContactId: vorschlagKontext.kontextContactId,
        kontextContactIds: vorschlagKontext.kontextContactIds,
        bereitsGewaehlt: attendees.map((a) => a.address),
    });
    const teilnehmerVorschlagWaehlen = (h: EmailRecipientHit) => {
        addAttendee(h.email, h.displayName);
        // Gehört die Adresse zu einem KMVS-Kontakt, wird er dem Termin auch
        // als Kontakt zugewiesen (Review PR #2237). Kollegen und das eigene
        // Postfach nicht — die machen keinen Kundentermin.
        const r = kontaktAusTeilnehmer(
            kontakte,
            autoKontakteRef.current,
            h,
            (id) => ({ id, name: h.displayName?.trim() || h.email }),
            interneAdressSet(appUsers, targetMailbox),
        );
        autoKontakteRef.current = r.auto;
        if (r.hinzugefuegt) kontakteSetzen(r.kontakte);
        // Mit Kontaktvorschlägen bleibt die Liste offen — mehrere
        // Ansprechpartner lassen sich nacheinander anklicken.
        if (!teilnehmerVorschlaege.hatKontext) setTeilnehmerListeOffen(false);
    };

    // T-267/T-269: eine leere oder halb getippte Uhrzeit (Edge meldet dann "")
    // ging früher als „…T:00" an die API und scheiterte mit 400.
    const uhrzeitFehlt = !allDay && (!uhrzeitGueltig(startTime) || !uhrzeitGueltig(endTime));
    const requiredOk =
        !!subject.trim() &&
        !!startDate &&
        !!endDate &&
        !uhrzeitFehlt &&
        (effectiveArt !== "kundentermin" || !!kunde);
    const canSubmit = requiredOk && !saving && !readOnly;

    // Start/Ende als Date aus dem Formular-Zustand (SP5): für die Lese-Übersicht
    // (zeitraumLabel erwartet das EXKLUSIVE Ganztags-Ende — Graph-Konvention)
    // und das Edit-Prefill des „Weitere Optionen"-Flows (INKLUSIVES Ende, so wie
    // es im Ende-Feld des Formulars steht).
    const formStartDate = (): Date | null => {
        if (!startDate) return null;
        const d = new Date(startDate);
        const t = allDay ? { h: 0, m: 0 } : parseZeit(startTime);
        d.setHours(t.h, t.m, 0, 0);
        return d;
    };
    const formEndDate = (exklusiv: boolean): Date | null => {
        if (!endDate) return null;
        const d = new Date(endDate);
        if (allDay) {
            if (exklusiv) d.setDate(d.getDate() + 1);
            d.setHours(0, 0, 0, 0);
        } else {
            const t = parseZeit(endTime);
            d.setHours(t.h, t.m, 0, 0);
        }
        return d;
    };

    const handleSave = async () => {
        if (!canSubmit) return;
        if (!targetMailbox) {
            setError("Bitte ein Postfach auswählen.");
            return;
        }
        if (!startDate || !endDate) {
            setError("Bitte Start- und Endzeit angeben.");
            return;
        }
        if (uhrzeitFehlt) {
            setError("Bitte Start- und End-Uhrzeit vollständig angeben.");
            return;
        }
        if (effectiveArt === "kundentermin" && !kunde) {
            setError("Bitte einen Kunden auswählen.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            // Payload-Bildung über die GETEILTEN Helfer (terminFormShared) — den
            // Create-Pfad nutzt der Light-Dialog identisch; das Update braucht
            // start/end/attendees separat für die PATCH-Dirty-Gates.
            const { start, end } = buildStartEnd(startDate, endDate, startTime, endTime, allDay);
            const attendeePayload = buildAttendeePayload(attendees, roomAddress, rooms);
            const recurrencePayload = buildRecurrencePayload(recurrence, startDate);
            const { isReminderOn: reminderOn, reminderMinutesBeforeStart: reminderMinutes } =
                reminderFromPreset(reminderPreset);

            let eventId: string;
            if (isEdit && editEventId) {
                // PO 28.09.2026: „Updates am Termin, die nichts mit dem Termin
                // direkt zu tun haben — Kundentermin/Austausch, Vorgang
                // zuweisen —, dürfen den Termin nicht updaten." Jeder PATCH an
                // einer Besprechung verschickt ein Update an alle Teilnehmer.
                // Graph wird deshalb NUR angefasst, wenn sich ein Feld des
                // Outlook-Termins wirklich geändert hat; Art, Farbe, Kontakte
                // und Vorgang leben allein im KMVS-Sidecar.
                const graphGeaendert = graphTerminGeaendert(
                    initialGraphFelderRef.current,
                    graphFelder,
                    {
                        body: bodyDirty,
                        teilnehmer: attendeesDirty,
                        serie: recurrenceDirty,
                        privat: privatDirty,
                    },
                );
                if (graphGeaendert) {
                    await calendarService.updateEvent(targetMailbox, editEventId, {
                        subject,
                        start,
                        end,
                        timeZone: "Europe/Berlin",
                        isAllDay: allDay,
                        // SP5/A9-Leitplanke: den Body nur zurückschreiben, wenn der
                        // User ihn tatsächlich geändert hat (PATCH-Semantik: weglassen
                        // = unangetastet) — ein reiner Titel-/Zeit-Edit zerstört so
                        // keine Teams-Einladung (HTML bleibt original).
                        body: bodyDirty ? body : undefined,
                        contentType: bodyDirty ? "HTML" : undefined,
                        location: location || undefined,
                        attendees: attendeesDirty ? attendeePayload : undefined,
                        // Serie nur senden, wenn der User sie angefasst hat (PATCH-Semantik:
                        // weglassen = unangetastet, null = auflösen). Schützt Fremd-/Master-
                        // Serien beim reinen Titel-Edit (Entscheidung G, Roundtrip-Beweis).
                        recurrence: recurrenceDirty ? recurrencePayload : undefined,
                        isReminderOn: reminderOn,
                        reminderMinutesBeforeStart: reminderMinutes,
                        // Nur senden, wenn der User das Haeckchen angefasst hat
                        // (PATCH-Semantik wie bei Body/Serie): sonst ueberschriebe ein
                        // Zeit-Edit eine in Outlook gesetzte Markierung.
                        istPrivat: privatDirty ? istPrivat : undefined,
                    });
                }
                eventId = editEventId;
            } else {
                const created = await calendarService.createEvent(
                    buildCreateEventInput({
                        mailbox: targetMailbox,
                        subject,
                        body,
                        startDate,
                        endDate,
                        startTime,
                        endTime,
                        allDay,
                        isOnlineMeeting: teamsAllowed && isOnlineMeeting,
                        location,
                        attendees,
                        roomAddress,
                        rooms,
                        recurrence,
                        reminderPreset,
                        istPrivat,
                    }),
                );
                eventId = created.id;
            }

            // Art und Kontakte am Sidecar (best-effort) — reine KMVS-Daten.
            //
            // Edit: nur schreiben, was sich geändert hat. Neuanlage: nur, wenn
            // der Nutzer die Art gewählt oder Kontakte gesetzt hat — sonst
            // bleibt die Server-Auto-Klassifikation aus den Teilnehmern
            // (classifyFromAttendees im POST-Pfad) stehen; ein clientseitiges
            // 'austausch' würde sie überschreiben (C1-Race).
            const zuSendendeKontakte = kontakte;
            const kontaktIdsJetzt = zuSendendeKontakte.map((k) => k.id).join(",");
            const artGeaendert = isEdit
                ? initialKmvsRef.current?.art !== terminArt
                : artGewaehlt || zuSendendeKontakte.length > 0;
            const kontakteGeaendert = isEdit
                ? artGeaendert || initialKmvsRef.current?.kontaktIds !== kontaktIdsJetzt
                : zuSendendeKontakte.length > 0;
            if (artGeaendert) {
                try {
                    await calendarTerminService.setTerminArt(targetMailbox, eventId, {
                        terminArt,
                        kundeId: terminArt === "kundentermin" ? kunde?.id ?? null : null,
                    });
                } catch (clErr) {
                    console.warn("[TerminFormWindow] Art-Kennzeichnung fehlgeschlagen", clErr);
                }
            }

            // Kontakt-Zuweisung (PO 21.09.2026) — NACH der Art, damit sie das
            // letzte Wort über `kundeId` hat: beide schreiben dasselbe Feld,
            // und maßgeblich ist die Liste im Picker. `artBeibehalten`: die Art
            // hat eben das Radio gesetzt, die Zuweisung darf sie nicht
            // überstimmen (sonst würde aus „Austausch" mit Kontakt ein
            // Kundentermin). Die leere Liste hebt die Zuweisung im Edit auf.
            if (kontakteGeaendert) {
                try {
                    await calendarTerminService.setTerminKontakte(
                        targetMailbox,
                        eventId,
                        zuSendendeKontakte.map((k) => k.id),
                        { artBeibehalten: true },
                    );
                } catch (kErr) {
                    console.warn("[TerminFormWindow] Kontakt-Zuweisung fehlgeschlagen", kErr);
                }
            }

            // SP6/A10: individuelle Farbe (best-effort wie die Art) — nur wenn
            // angefasst, damit ein reiner Titel-/Zeit-Edit keinen Extra-Request
            // auslöst.
            if (farbeDirty) {
                try {
                    await calendarTerminService.setTerminFarbe(targetMailbox, eventId, farbe);
                } catch (fbErr) {
                    console.warn("[TerminFormWindow] Farbe speichern fehlgeschlagen", fbErr);
                }
            }

            // K3 — die Verknüpfung nach dem Create ist NICHT mehr best-effort:
            // schlägt sie fehl, bleibt das Fenster offen und sagt es. Vorher
            // stand da „Termin angelegt", und die Verknüpfung fehlte kommentarlos.
            const gewaehlterVorgang = vorgangWahl.kind === "existing" ? vorgangWahl.vorgangId : null;
            if (!isEdit && (gewaehlterVorgang != null || linkMail != null)) {
                try {
                    if (gewaehlterVorgang != null) {
                        await calendarTerminService.setVorgang(targetMailbox, eventId, gewaehlterVorgang);
                        // … und an das To-do, aus dem heraus angelegt wurde
                        // (PO 18.09.2026) — Regel in `terminTodoBindung.ts`.
                        const todoZiel = todoFuerNeuenTermin({
                            linkTodoId,
                            linkVorgangId,
                            gewaehlterVorgangId: gewaehlterVorgang,
                        });
                        if (todoZiel != null) {
                            await calendarTerminService.setTodo(targetMailbox, eventId, todoZiel);
                        }
                        // P9: … und direkt im Anschluss die Post des Vorgangs.
                        await erbeVorgangsMails(targetMailbox, eventId, gewaehlterVorgang);
                    }
                    // K1/J3: aus der Mail-Leiste heraus angelegter Termin hängt
                    // danach automatisch an der auslösenden Mail.
                    if (linkMail != null) {
                        await calendarTerminService.linkMailToTermin(targetMailbox, eventId, linkMail);
                    }
                } catch (linkErr: any) {
                    console.error("[TerminFormWindow] Verknüpfung fehlgeschlagen", linkErr);
                    setWindowDirty(windowId, false);
                    requestCalendarReload();
                    setError(
                        "Termin wurde angelegt, die Verknüpfung ist aber fehlgeschlagen: " +
                            (linkErr?.message ?? "unbekannter Fehler"),
                    );
                    return;
                }
            }

            // K3 — „das Ergebnis nach dem Speichern nachladen": der Sidecar-Stand
            // wird frisch gelesen, statt ihn aus dem Formular zu raten. Rein
            // best-effort — ein fehlender Nachlade-Schritt ist kein Fehler.
            if (!isEdit && (gewaehlterVorgang != null || linkMail != null)) {
                try {
                    const frisch = await calendarTerminService.getMeta(targetMailbox, eventId);
                    setSidecarMeta(frisch);
                } catch {
                    /* Anzeige-Komfort; der Sidecar lädt beim Öffnen ohnehin selbst. */
                }
            }

            setWindowDirty(windowId, false);
            requestCalendarReload();
            if (isEdit) {
                closeWindow(windowId);
            } else {
                // „Neu erstellen": Erstell-Flow NACH dem Termin (braucht die
                // eventId), Kontakt vorbefüllt, Auto-Bind an den Termin.
                if (vorgangWahl.kind === "new") {
                    openVorgangCreateFlow(openWindow, {
                        initialContactId: kunde?.id ?? null,
                        initialContactName: kunde?.name ?? null,
                        onCreated: async (result) => {
                            await bindeAnTermin(targetMailbox, eventId, result);
                        },
                    });
                }
                // SP5/A11: Erfolgs-Zwischenschritt statt sofort schließen —
                // „Weitere Optionen" öffnet denselben Termin im Edit-Modus
                // (dort Notizen/Dokumente/Vorgang-Verknüpfung), „Fertig" schließt.
                setAngelegt({ id: eventId });
            }
        } catch (err: any) {
            console.error("[TerminFormWindow] Speichern fehlgeschlagen", err);
            setError(err?.message ?? "Konnte den Termin nicht speichern.");
        } finally {
            setSaving(false);
        }
    };

    // SP5/A11: „Weitere Optionen" — dasselbe Fenster im Edit-Modus des neu
    // angelegten Termins öffnen (stabile Fenster-ID termin-edit-<id>), das
    // Create-Fenster schließen. Das Edit-Prefill kommt aus dem Formular-Zustand;
    // Body/Teilnehmer/Serie lädt das Edit-Fenster ohnehin über getEvent nach.
    const openWeitereOptionen = () => {
        if (!angelegt) return;
        openTerminFormWindow(openWindow, {
            editEventId: angelegt.id,
            mailbox: targetMailbox,
            prefillEvent: {
                id: angelegt.id,
                title: subject,
                start: formStartDate() ?? new Date(),
                end: formEndDate(false) ?? new Date(),
                allDay,
                location,
                mailbox: targetMailbox,
            },
        });
        closeWindow(windowId);
    };

    /**
     * „Notiz" direkt nach dem Anlegen (PO 28.09.2026: Notizfeld → Notiz-Knopf).
     * Die Notiz-Seite hängt am Sidecar; den legt `ensureMeta` an. Das Fenster
     * wandert ins Dock, damit es nicht über der Seite liegt.
     */
    const [notizFehler, setNotizFehler] = useState<string | null>(null);
    const oeffneNotiz = async () => {
        if (!angelegt) return;
        setNotizFehler(null);
        try {
            const meta = await calendarTerminService.ensureMeta(targetMailbox, angelegt.id);
            if (meta.terminId == null) throw new Error("kein Sidecar");
            minimizeWindow(windowId);
            navigate(`/termine/notizen/${meta.terminId}`);
        } catch (err) {
            console.error("[TerminFormWindow] Notiz-Seite öffnen fehlgeschlagen", err);
            setNotizFehler("Die Notiz-Seite konnte nicht geöffnet werden.");
        }
    };

    const handleDelete = async () => {
        if (!isEdit || !editEventId || !targetMailbox || readOnly) return;
        const ok = await bestaetigen({
            titel: "Termin löschen?",
            text: "Der Termin wird auch im Outlook-Kalender gelöscht.",
            ja: "Löschen",
            ton: "danger",
        });
        if (!ok) return;
        setDeleting(true);
        setError(null);
        try {
            await calendarService.deleteEvent(targetMailbox, editEventId);
            setWindowDirty(windowId, false);
            requestCalendarReload();
            closeWindow(windowId);
        } catch (err: any) {
            console.error("[TerminFormWindow] Löschen fehlgeschlagen", err);
            setError(err?.message ?? "Konnte den Termin nicht löschen.");
        } finally {
            setDeleting(false);
        }
    };

    // "Ganze Reihe bearbeiten" (D): Master-Event editierbar öffnen.
    const openMaster = () => {
        if (!seriesInstance?.masterId || !targetMailbox) return;
        openTerminFormWindow(openWindow, {
            editEventId: seriesInstance.masterId,
            mailbox: targetMailbox,
            canEdit,
        });
        closeWindow(windowId);
    };

    const roomOptions = useMemo(() => {
        // Ein am Termin gebuchter Raum, der nicht (mehr) in der Liste steht
        // (z. B. ausgeblendete Ressource), bleibt als Option sichtbar — sonst
        // zeigte das Feld still „kein Raum“.
        if (roomAddress && !rooms.some((r) => r.emailAddress.toLowerCase() === roomAddress.toLowerCase())) {
            return [
                ...rooms,
                { displayName: roomAddress, emailAddress: roomAddress, capacity: null, building: null, floorLabel: null },
            ];
        }
        return rooms;
    }, [rooms, roomAddress]);

    // Raum-Verfügbarkeit im gewählten Zeitraum — beim Bearbeiten zählt der
    // eigene Termin nicht als Belegung.
    const raumVerfuegbarkeit = useRaumVerfuegbarkeit({
        aktiv: !readOnly && rooms.length > 0,
        startDate,
        endDate,
        startTime,
        endTime,
        allDay,
        eigenerTermin:
            isEdit && editEventId && targetMailbox
                ? { mailbox: targetMailbox, eventId: editEventId }
                : null,
    });
    const gewaehlterRaumBelegt = roomAddress
        ? raumVerfuegbarkeit.nachRaum[roomAddress.toLowerCase()]
        : undefined;

    // SP5/A11: Erfolgs-Zwischenschritt nach der Neu-Anlage — Notiz-/Sidecar-
    // Optionen gibt es erst, wenn der Termin existiert („Weitere Optionen").
    if (angelegt) {
        return (
            <div className={styles.root}>
                <div className={styles.body}>
                    <div className={styles.successBox}>
                        <span className={styles.successIcon}>
                            <CheckmarkCircle24Regular />
                        </span>
                        <h3 className={styles.successTitle}>Termin angelegt</h3>
                        <p className={styles.successText}>
                            „{subject.trim() || "Termin"}" wurde im Kalender gespeichert.
                        </p>
                        <p className={styles.successHint}>
                            „Notiz" öffnet die Notiz-Seite des Termins — sie erscheint auch im
                            verknüpften Vorgang. Über „Weitere Optionen" lassen sich Dokumente
                            und die Vorgang-Verknüpfung ergänzen.
                        </p>
                        {notizFehler && <div className={styles.errorBox}>{notizFehler}</div>}
                    </div>
                </div>
                <div className={styles.footerBar}>
                    <footer className={styles.footer}>
                        <button
                            type="button"
                            className={styles.ghostBtn}
                            onClick={() => closeWindow(windowId)}
                        >
                            Fertig
                        </button>
                        <div className={styles.footerRight}>
                        <button
                            type="button"
                            className={styles.ghostBtn}
                            onClick={() => void oeffneNotiz()}
                        >
                            Notiz
                        </button>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={openWeitereOptionen}
                        >
                            <Open16Regular />
                            Weitere Optionen
                        </button>
                        </div>
                    </footer>
                </div>
            </div>
        );
    }

    // Lese-Übersicht (SP5, Plan 3.6.3): Zeitraum für zeitraumLabel (exklusives
    // Ganztags-Ende, Graph-Konvention) aus dem aktuellen Formular-Zustand.
    const uebersichtStart = formStartDate();
    const uebersichtEnde = formEndDate(true);
    const reminderLabel = REMINDER_OPTIONS.find((o) => o.value === reminderPreset)?.label;
    // SP5/A9: HTML-Body (Teams-Einladung) lesend rendern statt roher Tags.
    // T-269: auch der aus einer Vorlage übernommene HTML-Body wird gerendert
    // statt als Quelltext in der Textarea zu stehen.
    const istHtmlBody = (isEdit || !!vorlage) && siehtAusWieHtml(body, bodyContentType);

    return (
        <div className={styles.root}>
            <div className={styles.body}>
                {/* Neuanlage: IN WELCHEM POSTFACH, ganz oben (PO 22.09.2026 —
                    „bei neuen Termin immer die eigene Adresse verwenden und
                    ganz oben kann man das noch ändern"). Sie beantwortet die
                    Frage, die vor allen anderen kommt. */}
                {!isEdit && (writeMailboxes.length > 0 || targetMailbox) && (
                    <div className={styles.field}>
                        <label className={styles.label}>Postfach</label>
                        <select
                            className={styles.input}
                            value={targetMailbox}
                            aria-label="In welchem Postfach der Termin angelegt wird"
                            onChange={(e) => {
                                userTouchedMailboxRef.current = true;
                                setCreateMailbox(e.target.value);
                            }}
                        >
                            {!writeMailboxes.some(
                                (m) => m.mailboxAddress.toLowerCase() === targetMailbox.toLowerCase(),
                            ) &&
                                targetMailbox && <option value={targetMailbox}>{targetMailboxLabel}</option>}
                            {writeMailboxes.map((m) => (
                                <option key={m.mailboxAddress} value={m.mailboxAddress}>
                                    {m.displayName || m.mailboxAddress}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                {!isEdit && vorlage && (
                    <div className={styles.mailboxHint} role="note">
                        Kopie von „{vorlage.titel.trim() || "Termin"}“ — Notizen, Dokumente und
                        To-dos bleiben am Original. Beim Speichern entsteht ein neuer Termin; die
                        Teilnehmer erhalten eine Einladung.
                    </div>
                )}
                {!canEdit && (
                    <div className={styles.readOnlyBanner}>
                        Fremder Kalender — nur Lesezugriff. Speichern und Verknüpfen sind gesperrt.
                    </div>
                )}
                {isSeriesInstance && (
                    <div className={styles.seriesBanner}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <Alert20Regular />
                            Dies ist ein Termin einer Serie. Einzeltermine sind hier nur lesbar.
                        </span>
                        {seriesInstance?.masterId && canEdit && (
                            <button type="button" className={styles.seriesOpenBtn} onClick={openMaster}>
                                <Open16Regular />
                                Ganze Reihe bearbeiten
                            </button>
                        )}
                    </div>
                )}

                {/* Lese-Übersicht (SP5, Plan 3.6.3): erst die Übersicht aller
                    gesetzten Felder inkl. Notizen, Bearbeitung darunter. */}
                {isEdit && (
                    <div className={styles.overviewBox}>
                        <h3 className={styles.overviewTitle}>Übersicht</h3>
                        {uebersichtStart && uebersichtEnde && (
                            <div className={styles.overviewRow}>
                                <span className={styles.overviewLbl}>Zeit</span>
                                <span>{zeitraumLabel(uebersichtStart, uebersichtEnde, allDay)}</span>
                            </div>
                        )}
                        <div className={styles.overviewRow}>
                            <span className={styles.overviewLbl}>Art</span>
                            <span>
                                {ART_LABEL[effectiveArt]}
                                {/* Auch bei „Sonstige" den Kontakt nennen — dort
                                    hängt er genauso am Termin. */}
                                {effectiveArt !== "austausch" && kunde ? ` · ${kunde.name}` : ""}
                            </span>
                        </div>
                        {/* SP6/A10: gesetzte individuelle Farbe in der Übersicht
                            (Plan 3.6.3 nennt sie als gesetztes Feld). */}
                        {farbe && (
                            <div className={styles.overviewRow}>
                                <span className={styles.overviewLbl}>Farbe</span>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    <span
                                        aria-hidden
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 999,
                                            background: farbe,
                                            display: "inline-block",
                                            border: "1px solid rgba(0,0,0,0.15)",
                                        }}
                                    />
                                    {terminFarbLabel(farbe)}
                                </span>
                            </div>
                        )}
                        {organizer && (
                            <div className={styles.overviewRow}>
                                <span className={styles.overviewLbl}>Organisator</span>
                                <span>{organizer.name || organizer.address}</span>
                            </div>
                        )}
                        {location.trim() !== "" && (
                            <div className={styles.overviewRow}>
                                <span className={styles.overviewLbl}>Ort</span>
                                <span>{location}</span>
                            </div>
                        )}
                        {isOnlineMeeting && (
                            <div className={styles.overviewRow}>
                                <span className={styles.overviewLbl}>Teams</span>
                                {onlineMeetingUrl ? (
                                    <a
                                        href={onlineMeetingUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: REFINED.aub, fontWeight: 600 }}
                                    >
                                        Jetzt teilnehmen
                                    </a>
                                ) : (
                                    <span>Online-Meeting</span>
                                )}
                            </div>
                        )}
                        {(recurrence != null || isSeriesInstance) && (
                            <div className={styles.overviewRow}>
                                <span className={styles.overviewLbl}>Serie</span>
                                <span>Serientermin</span>
                            </div>
                        )}
                        {reminderPreset !== "none" && reminderLabel && (
                            <div className={styles.overviewRow}>
                                <span className={styles.overviewLbl}>Erinnerung</span>
                                <span>{reminderLabel}</span>
                            </div>
                        )}
                        {attendees.length > 0 && (
                            <div className={styles.overviewRow}>
                                <span className={styles.overviewLbl}>Teilnehmer</span>
                                <span>{attendees.length}</span>
                            </div>
                        )}
                        {linkedVorgangId != null && (
                            <div className={styles.overviewRow}>
                                <span className={styles.overviewLbl}>Vorgang</span>
                                <span>verknüpft (#{linkedVorgangId})</span>
                            </div>
                        )}
                        {linkedTodoId != null && (
                            <div className={styles.overviewRow}>
                                <span className={styles.overviewLbl}>To-do</span>
                                <span>verknüpft (#{linkedTodoId})</span>
                            </div>
                        )}
                        {vorhandeneNotizen.length > 0 && (
                            <div className={styles.overviewRow} style={{ alignItems: "flex-start" }}>
                                <span className={styles.overviewLbl}>Notizen</span>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 4,
                                        minWidth: 0,
                                        flex: 1,
                                    }}
                                >
                                    {vorhandeneNotizen.slice(0, 3).map((n) => (
                                        <div key={n.id} className={styles.overviewNote}>
                                            {n.text}
                                        </div>
                                    ))}
                                    {vorhandeneNotizen.length > 3 && (
                                        <span style={{ fontSize: "0.8rem", color: REFINED.ink2 }}>
                                            +{vorhandeneNotizen.length - 3} weitere — siehe Notizen unten
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Teilnehmer (E-Mail) und Kontakte — PO 28.09.2026: direkt unter
                    dem Postfach, vor Art und Titel. Die Chips zeigen die
                    E-Mail-Adresse unter dem Namen. */}
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                        <span className={styles.sectionTitleIcon}>
                            <People20Regular />
                        </span>
                        Teilnehmer
                    </h3>
                </div>
                <div className={styles.attendeeRow}>
                    <div ref={teilnehmerAnkerRef} style={{ flex: 1, display: "flex" }}>
                        <input
                            className={styles.input}
                            style={{ flex: 1 }}
                            value={attendeeInput}
                            disabled={readOnly}
                            placeholder="Name oder E-Mail suchen …"
                            data-teilnehmer-eingabe
                            onChange={(e) => {
                                setAttendeeInput(e.target.value);
                                setTeilnehmerListeOffen(true);
                            }}
                            onFocus={() => setTeilnehmerListeOffen(true)}
                            // Erneuter Klick bei fokussiertem Feld holt die
                            // Vorschläge zurück (nach Escape oder Auswahl).
                            onClick={() => setTeilnehmerListeOffen(true)}
                            onBlur={() => setTeilnehmerListeOffen(false)}
                            onKeyDown={(e) => {
                                if (teilnehmerVorschlaege.pfeilTaste(e.key)) {
                                    e.preventDefault();
                                    return;
                                }
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    const markiert = teilnehmerVorschlaege.markierterTreffer;
                                    if (markiert) {
                                        teilnehmerVorschlagWaehlen(markiert);
                                    } else {
                                        addAttendee(attendeeInput);
                                    }
                                }
                                if (e.key === "Escape" && teilnehmerVorschlaege.zeigen) {
                                    // Nur die Liste schließen, nicht das Fenster.
                                    e.stopPropagation();
                                    setTeilnehmerListeOffen(false);
                                }
                            }}
                        />
                    </div>
                    <button
                        type="button"
                        className={styles.addBtn}
                        disabled={readOnly || !attendeeInput.trim()}
                        onClick={() => addAttendee(attendeeInput)}
                    >
                        + Hinzufügen
                    </button>
                </div>
                <EmpfaengerVorschlagsListe
                    vorschlaege={teilnehmerVorschlaege}
                    ankerRef={teilnehmerAnkerRef}
                    texte={TEILNEHMER_VORSCHLAG_TEXTE}
                    onSelect={teilnehmerVorschlagWaehlen}
                />
                {/* SP5/A8: kollabierte Chip-Liste (4 + „+N weitere") — geteilte
                    Komponente mit dem Peek-Popover. */}
                <AttendeeChips
                    attendees={attendees}
                    onRemove={readOnly ? undefined : removeAttendee}
                    style={{ marginTop: 8 }}
                    mitAdresse
                />
                {inviteWarnings.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {inviteWarnings.map((w) => {
                            const name =
                                interneTeilnehmer.find(
                                    (t) => t.mailbox.toLowerCase() === w.mailbox.toLowerCase(),
                                )?.name || w.mailbox;
                            const wann = moment(w.from).format("DD.MM.YYYY");
                            const typLabel = ABWESENHEIT_LABEL[w.type] ?? "abwesend";
                            return (
                                <div key={w.mailbox} className={styles.warnBox}>
                                    ⚠ Achtung: {name} ist am {wann} {typLabel}. Einladen bleibt möglich.
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Kontakte zuweisen — seit PO 28.09.2026 bei JEDER Art, auch bei
                    „Austausch". Die Zuweisung bringt den Termin in den Reiter
                    „Termine" des Kontakts (und seines Umfelds). Mehrere Kontakte,
                    der erste ist der Hauptkontakt (`kundeId`). Die Art bestimmt
                    allein das Radio: gespeichert wird mit `artBeibehalten`. */}
                <TerminKontaktePicker
                    value={kontakte}
                    onChange={kontakteVonHand}
                    disabled={readOnly}
                    label="Kontakte zuweisen"
                />

                {/* Art & Titel — seit PO 28.09.2026 wieder in beiden Modi: auch
                    die Neuanlage fragt Kundentermin/Austausch/Sonstige. Ohne
                    Wahl (und ohne Kontakt) klassifiziert der Server weiterhin
                    aus den Teilnehmern. */}
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                        <span className={styles.sectionTitleIcon}>
                            <CalendarLtr20Regular />
                        </span>
                        Art &amp; Titel
                    </h3>
                </div>
                <div className={styles.radioRow}>
                    <label className={styles.radioLabel}>
                        <input
                            type="radio"
                            name={`terminArt-${windowId}`}
                            checked={terminArt === "austausch"}
                            disabled={readOnly}
                            onChange={() => setTerminArt("austausch")}
                        />
                        Austausch
                    </label>
                    <label className={styles.radioLabel}>
                        <input
                            type="radio"
                            name={`terminArt-${windowId}`}
                            checked={terminArt === "kundentermin"}
                            disabled={readOnly}
                            onChange={() => setTerminArt("kundentermin")}
                        />
                        Kundentermin
                    </label>
                    {/* PO 21.09.2026 — weder Kundengespräch noch interne
                        Besprechung (Notar, Gericht, Gutachter). Ein Kontakt
                        darf daran hängen, der Picker bleibt sichtbar. */}
                    <label className={styles.radioLabel}>
                        <input
                            type="radio"
                            name={`terminArt-${windowId}`}
                            checked={terminArt === "sonstige"}
                            disabled={readOnly}
                            onChange={() => setTerminArt("sonstige")}
                        />
                        Sonstige
                    </label>
                </div>
                {/* Privat (Outlook-Markierung) und — SP6/A10, Entscheidung 65 —
                    die individuelle Farbe (Preset-Palette, KMVS-intern, Outlook
                    sieht sie nicht; gespeichert mit „Speichern", best-effort
                    wie die Art). Seit PO 28.09.2026 auch in der Neuanlage. */}
                <div className={styles.field}>
                    <label className={styles.checkRow}>
                        <input
                            type="checkbox"
                            checked={istPrivat}
                            disabled={readOnly}
                            onChange={(e) => {
                                setIstPrivat(e.target.checked);
                                setPrivatDirty(true);
                            }}
                        />
                        Privater Termin
                    </label>
                </div>
                <div className={styles.field}>
                    <label className={styles.label}>Farbe</label>
                    <TerminFarbPicker
                        value={farbe}
                        disabled={readOnly}
                        onSelect={(hex) => {
                            setFarbe(hex);
                            setFarbeDirty(true);
                        }}
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.label}>
                        Titel<span className={styles.requiredMark}>*</span>
                    </label>
                    <input
                        className={styles.input}
                        value={subject}
                        disabled={readOnly}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="z. B. Jahresgespräch — Brammers GmbH"
                        autoFocus={!readOnly}
                    />
                </div>
                {/* Zeit & Ort */}
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                        <span className={styles.sectionTitleIcon}>
                            <Clock20Regular />
                        </span>
                        Zeit &amp; Ort
                    </h3>
                </div>
                <div className={styles.fieldRowTwo}>
                    <div className={styles.field}>
                        <label className={styles.label}>
                            Start<span className={styles.requiredMark}>*</span>
                        </label>
                        <input
                            type="date"
                            className={styles.input}
                            value={startDate ? ymd(startDate) : ""}
                            disabled={readOnly}
                            onChange={(e) =>
                                aendereStartDatum(e.target.value ? new Date(e.target.value) : null)
                            }
                        />
                    </div>
                    {!allDay && (
                        <div className={styles.field}>
                            <label className={styles.label}>Uhrzeit</label>
                            <input
                                type="time"
                                className={styles.input}
                                value={startTime}
                                disabled={readOnly}
                                onChange={(e) => aendereStartZeit(e.target.value)}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.fieldRowTwo}>
                    <div className={styles.field}>
                        <label className={styles.label}>
                            Ende<span className={styles.requiredMark}>*</span>
                        </label>
                        <input
                            type="date"
                            className={styles.input}
                            value={endDate ? ymd(endDate) : ""}
                            disabled={readOnly}
                            onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                        />
                    </div>
                    {!allDay && (
                        <div className={styles.field}>
                            <label className={styles.label}>Uhrzeit</label>
                            <input
                                type="time"
                                className={styles.input}
                                value={endTime}
                                disabled={readOnly}
                                onChange={(e) => setEndTime(e.target.value)}
                            />
                        </div>
                    )}
                </div>
                <label className={styles.checkRow}>
                    <input
                        type="checkbox"
                        checked={allDay}
                        disabled={readOnly}
                        onChange={(e) => setAllDay(e.target.checked)}
                    />
                    Ganztägig
                </label>
                {uhrzeitFehlt && !readOnly && (
                    <span className={styles.reqHint}>
                        <Info16Regular />
                        Bitte Start- und End-Uhrzeit vollständig angeben (z. B. 09:30).
                    </span>
                )}
                {!isEdit && (
                    <label
                        className={styles.checkRow}
                        style={teamsAllowed ? undefined : { cursor: "not-allowed", opacity: 0.6 }}
                        title={
                            teamsAllowed
                                ? undefined
                                : `${targetMailboxLabel} darf keine Teams-Besprechungen anlegen.`
                        }
                    >
                        <input
                            type="checkbox"
                            checked={teamsAllowed && isOnlineMeeting}
                            disabled={readOnly || !teamsAllowed}
                            onChange={(e) => setIsOnlineMeeting(e.target.checked)}
                        />
                        Online-Meeting (Teams)
                    </label>
                )}
                <div className={styles.field}>
                    <label className={styles.label}>Ort</label>
                    <input
                        className={styles.input}
                        value={location}
                        disabled={readOnly}
                        onChange={(e) => {
                            ortVomNutzerRef.current = true;
                            setLocation(e.target.value);
                        }}
                        placeholder="z. B. Büro / Adresse"
                    />
                </div>
                {roomOptions.length > 0 && (
                    <div className={styles.field}>
                        <label className={styles.label}>Raum</label>
                        <select
                            className={styles.input}
                            value={roomAddress}
                            disabled={readOnly}
                            onChange={(e) => {
                                setRoomAddress(e.target.value);
                                setAttendeesDirty(true);
                            }}
                        >
                            <option value="">— kein Raum —</option>
                            {roomOptions.map((r) => {
                                const v = raumVerfuegbarkeit.nachRaum[r.emailAddress.toLowerCase()];
                                const istGewaehlt =
                                    r.emailAddress.toLowerCase() === roomAddress.toLowerCase();
                                return (
                                    <option
                                        key={r.emailAddress}
                                        value={r.emailAddress}
                                        // Belegte Räume sind nicht wählbar (Exchange lehnt
                                        // die Buchung ab); der bereits gewählte bleibt
                                        // sichtbar, damit der Hinweis darunter Sinn ergibt.
                                        disabled={!raumWaehlbar(v) && !istGewaehlt}
                                    >
                                        {raumOptionText(r, v)}
                                    </option>
                                );
                            })}
                        </select>
                        {!readOnly && raumVerfuegbarkeit.fehler && (
                            <div className={styles.warnBox} role="status">
                                Verfügbarkeit gerade nicht prüfbar — die Räume sind ohne Belegung aufgeführt.
                            </div>
                        )}
                        {!readOnly && gewaehlterRaumBelegt?.status === "belegt" && (
                            <div className={styles.warnBox} role="alert">
                                ⚠ Der gewählte Raum ist in diesem Zeitraum belegt (
                                {belegungText(gewaehlterRaumBelegt.belegt)}). Bitte Zeit ändern oder
                                einen anderen Raum wählen — Microsoft lehnt die Buchung sonst ab.
                            </div>
                        )}
                    </div>
                )}

                {/* Serientermin (SP4) — Picker nutzt RdDatePicker (Konsistenz B2). */}
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                        <span className={styles.sectionTitleIcon}>
                            <ArrowRepeatAll20Regular />
                        </span>
                        Serientermin
                    </h3>
                </div>
                <RecurrencePicker
                    value={recurrence}
                    startDate={startDate ?? new Date()}
                    disabled={readOnly}
                    onChange={(rec) => {
                        setRecurrence(rec);
                        setRecurrenceDirty(true);
                    }}
                />

                {/* Erinnerung (SP4) */}
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                        <span className={styles.sectionTitleIcon}>
                            <Alert20Regular />
                        </span>
                        Erinnerung
                    </h3>
                </div>
                <div className={styles.field}>
                    <label className={styles.label}>Erinnern</label>
                    <select
                        className={styles.input}
                        value={reminderPreset}
                        disabled={readOnly}
                        onChange={(e) => setReminderPreset(e.target.value as ReminderPreset)}
                    >
                        {REMINDER_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Beschreibung */}
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                        <span className={styles.sectionTitleIcon}>
                            <TextDescription20Regular />
                        </span>
                        Beschreibung
                    </h3>
                </div>
                {/* SP5/A9 (Entscheidung 64): HTML-Bodies (Teams-Einladungen)
                    sanitisiert RENDERN (SandboxedMailBody: DOMPurify + Remote-
                    Block wie Mail) mit viel Platz; „Text bearbeiten" öffnet die
                    Plain-Textarea. Nur-Text bleibt direkt die Textarea. */}
                {istHtmlBody && !bodyEditMode ? (
                    <>
                        <div className={styles.htmlBodyBox}>
                            <SandboxedMailBody html={body} minHeight={280} />
                        </div>
                        {!readOnly && (
                            <button
                                type="button"
                                className={styles.addBtn}
                                style={{ alignSelf: "flex-start" }}
                                onClick={() => setBodyEditMode(true)}
                            >
                                Text bearbeiten
                            </button>
                        )}
                    </>
                ) : (
                    <textarea
                        className={styles.textarea}
                        value={body}
                        disabled={readOnly}
                        style={bodyEditMode ? { minHeight: 180 } : undefined}
                        onChange={(e) => {
                            setBody(e.target.value);
                            setBodyDirty(true);
                        }}
                        placeholder="Worum geht es?"
                    />
                )}

                {/* Vorgang (nur Neuanlage; im Edit übernimmt das der Sidecar
                    unten). Aus dem früheren Light-Dialog übernommen
                    (PO 28.09.2026): bestehendem zuordnen oder neu erstellen —
                    der Erstell-Flow startet erst nach dem Speichern. */}
                {!isEdit && (
                    <>
                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>
                                <span className={styles.sectionTitleIcon}>
                                    <TaskListSquareLtr20Regular />
                                </span>
                                Vorgang
                            </h3>
                        </div>
                        {vorgangWahl.kind === "none" && !vorgangPickerOpen && (
                            <div className={styles.attendeeRow}>
                                <button
                                    type="button"
                                    className={styles.addBtn}
                                    onClick={() => setVorgangPickerOpen(true)}
                                >
                                    Bestehendem zuordnen
                                </button>
                                <button
                                    type="button"
                                    className={styles.addBtn}
                                    onClick={() => setVorgangWahl({ kind: "new" })}
                                >
                                    Neu erstellen
                                </button>
                            </div>
                        )}
                        {vorgangWahl.kind === "none" && vorgangPickerOpen && (
                            <div className="cal-rd" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <VorgangSuchListe
                                    contactId={kunde?.id ?? contactId ?? null}
                                    onPick={(v) => {
                                        setVorgangWahl({
                                            kind: "existing",
                                            vorgangId: v.id,
                                            label: vorgangLabel(v.id, v.titel),
                                        });
                                        setVorgangPickerOpen(false);
                                    }}
                                />
                                <button
                                    type="button"
                                    className={styles.addBtn}
                                    style={{ alignSelf: "flex-start" }}
                                    onClick={() => setVorgangPickerOpen(false)}
                                >
                                    Abbrechen
                                </button>
                            </div>
                        )}
                        {vorgangWahl.kind !== "none" && (
                            <div className={styles.attendeeRow}>
                                <span style={{ flex: 1, fontSize: "0.92rem", fontWeight: 600 }}>
                                    {vorgangWahl.kind === "existing"
                                        ? `Vorgang: ${vorgangWahl.label}`
                                        : "Neuer Vorgang wird nach dem Speichern erstellt"}
                                </span>
                                <button
                                    type="button"
                                    className={styles.addBtn}
                                    onClick={() => {
                                        setVorgangWahl({ kind: "none" });
                                        setVorgangPickerOpen(false);
                                    }}
                                >
                                    entfernen
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Sidecar (nur Edit): Notizen/Docs/Vorgang/Todo. SP8/A17: der
                    Teilnehmer-Kontext füttert die Kontakt-Ableitung beim
                    „+ Neuen Vorgang erstellen" (interne Adressen zählen nicht). */}
                {isEdit && editEventId && targetMailbox && (
                    <div className={styles.sidecarSection}>
                        <TerminSidecar
                            mailbox={targetMailbox}
                            eventId={editEventId}
                            // E10: die VORGANGS-Verknüpfung braucht nur Leserecht am
                            // Kalender — sie schreibt KMVS-Metadaten am Sidecar.
                            // Notizen und Dokumente bleiben an `graphReadOnly`,
                            // weil ihre Endpoints weiterhin Schreibrecht verlangen.
                            graphReadOnly={readOnly}
                            kmvsReadOnly={false}
                            teilnehmerKontext={{ attendees, organizer }}
                            // Der Sprung auf den verknüpften Vorgang schickt
                            // dieses Fenster vorher ins Dock — sonst läge es
                            // über der Vorgangs-Seite bzw. löste die Rückfrage
                            // des WindowNavigationGuard aus.
                            fensterId={windowId}
                        />
                    </div>
                )}

                {error && <div className={styles.errorBox}>{error}</div>}
            </div>

            <div className={styles.footerBar}>
                <footer className={styles.footer}>
                    <div style={{ display: "flex", gap: 12 }}>
                        <button type="button" className={styles.ghostBtn} onClick={() => closeWindow(windowId)}>
                            Abbrechen
                        </button>
                        {isEdit && !readOnly && (
                            <button
                                type="button"
                                className={styles.deleteBtn}
                                disabled={deleting || saving}
                                onClick={handleDelete}
                            >
                                <Delete20Regular />
                                {deleting ? "Lösche…" : "Löschen"}
                            </button>
                        )}
                    </div>
                    {!readOnly && (
                        <div className={styles.footerRight}>
                            {!requiredOk && (
                                <span className={styles.reqHint}>
                                    <Info16Regular />
                                    Bitte Pflichtfelder ausfüllen
                                </span>
                            )}
                            <button
                                type="button"
                                className={styles.primaryBtn}
                                disabled={!canSubmit}
                                onClick={handleSave}
                            >
                                <Save20Regular />
                                {saving ? "Speichere…" : "Speichern"}
                            </button>
                        </div>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default TerminFormWindow;
